/**
 * WolfoxWidget — embeds a Wolfox audio briefing player.
 */
import React, { useEffect, useState } from 'react';
import apiHelper from '../../utils/apiHelper';

const WIDGET_SCRIPT_SRC = 'https://app.wolfox.ai/widget.js';

const TOKEN_CACHE_KEY = 'wolfox_embed_token';
const TOKEN_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

const MAX_ATTEMPTS = 5;
const RETRY_BASE_MS = 1000; // 1s, 2s, 4s, 8s

function readCachedToken() {
    try {
        const raw = sessionStorage.getItem(TOKEN_CACHE_KEY);
        if (!raw) return null;
        const { token, ts } = JSON.parse(raw);
        if (!token || !ts) return null;
        if (Date.now() - ts > TOKEN_CACHE_TTL_MS) return null;
        return token;
    } catch {
        return null;
    }
}

function writeCachedToken(token) {
    try {
        sessionStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify({ token, ts: Date.now() }));
    } catch {
    }
}

let tokenPromise = null;
function resolveEmbedToken() {
    if (
        process.env.NODE_ENV === 'development' &&
        process.env.REACT_APP_WOLFOX_EMBED_TOKEN
    ) {
        return Promise.resolve(process.env.REACT_APP_WOLFOX_EMBED_TOKEN);
    }
    const cached = readCachedToken();
    if (cached) return Promise.resolve(cached);
    if (!tokenPromise) {
        // Resolves with null only when the API reports no token is configured — that
        // is terminal. Everything else rejects so the caller can retry.
        tokenPromise = apiHelper
            .get('/api/config/wolfox-token')
            .then((response) => {
                tokenPromise = null;
                const token = response?.data?.token || null;
                if (!token) return null;
                writeCachedToken(token);
                return token;
            })
            .catch((err) => {
                tokenPromise = null;
                throw err;
            });
    }
    return tokenPromise;
}

function WolfoxWidget({ type = 'daily-policy', briefing = 'latest' }) {
    const [token, setToken] = useState(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let active = true;
        let timer = null;

        const attempt = (n) => {
            resolveEmbedToken().then(
                (t) => {
                    if (!active) return;
                    if (t) setToken(t);
                    else setFailed(true);
                },
                (err) => {
                    if (!active) return;
                    if (n >= MAX_ATTEMPTS) {
                        console.warn(
                            `[WolfoxWidget] Could not resolve an embed token after ${n} attempts: ${err.message}`
                        );
                        setFailed(true);
                        return;
                    }
                    timer = setTimeout(() => attempt(n + 1), RETRY_BASE_MS * 2 ** (n - 1));
                }
            );
        };

        attempt(1);
        return () => {
            active = false;
            if (timer) clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        if (!token) return undefined;
        const script = document.createElement('script');
        script.src = WIDGET_SCRIPT_SRC;
        script.async = true;
        script.onerror = () => {
            console.warn(
                `[WolfoxWidget] Failed to load ${WIDGET_SCRIPT_SRC}. ` +
                'Check network/CSP access and that this domain is whitelisted with Wolfox.'
            );
            setFailed(true);
        };
        document.body.appendChild(script);
        return () => {
            if (script.parentNode) script.parentNode.removeChild(script);
        };
    }, [token]);

    if (failed) {
        return (
            <div
                className="wolfox-embed-unavailable"
                role="status"
                style={{ padding: '16px', color: '#888', fontStyle: 'italic' }}
            >
                This content is currently unavailable. Please try again later.
            </div>
        );
    }

    if (!token) return null;

    return (
        <div
            className="wolfox-embed-container"
            data-widget-type={type}
            data-token={token}
            data-briefing={briefing || 'latest'}
            role="region"
            aria-label="Audio briefing"
        />
    );
}

export default WolfoxWidget;
